import QuizBanners from "@/components/banners/quiz-banners";
import EmptyState from "@/components/empty-state";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Button } from "@/components/ui/button";
import { useApplicationByEdition } from "@/hooks/application-by-edition.hook";
import { useCurrentUserProfile } from "@/hooks/current-user-profile.hook";
import { useDimensionByQuizId } from "@/hooks/dimension-by-quiz-id.hook";
import { useQuizById } from "@/hooks/quiz-by-id.hook";
import { mergeWithConfig } from "@/lib/dimensions";
import { getMessageFromError } from "@/lib/utils";
import { currentEditionIdAtom } from "@/store/edition-id.store";
import { Home2 } from "iconsax-react";
import { useAtomValue } from "jotai";
import { Link, useParams } from "react-router-dom";
import QuizDimensionHeader from "./quiz-dimension-header";
import QuizDimensionPageSkeleton from "./quiz-dimension-page-skeleton";
import TopicAccordionItem from "./topic-accordion-item";

const QuizDimensionPage = () => {
  const editionId = useAtomValue(currentEditionIdAtom);

  const { dimensionId } = useParams();

  const { data: userProfile } = useCurrentUserProfile();

  const { data: application } = useApplicationByEdition({
    companyId: userProfile?.companyId || "",
    editionId,
  });

  const { data: quiz } = useQuizById({
    companyId: userProfile?.companyId || "",
    applicationQuizId: application?.quizId || "",
  });

  const {
    data: dimensionSummary,
    isLoading: dimensionSummaryIsLoading,
    error: dimensionSummaryError,
  } = useDimensionByQuizId({
    companyId: userProfile?.companyId || "",
    applicationQuizId: application?.quizId || "",
    dimension: dimensionId || "",
  });

  const dimension = dimensionSummary ? mergeWithConfig(dimensionSummary) : null;

  if (dimensionSummaryIsLoading) {
    return <QuizDimensionPageSkeleton />;
  }

  if (dimensionSummaryError) {
    return (
      <EmptyState
        className="m-auto"
        message={getMessageFromError(dimensionSummaryError)}
      >
        <Button variant={"secondary"} className="mt-4" asChild>
          <Link to={"/"}>Volver a la página principal</Link>
        </Button>
      </EmptyState>
    );
  }

  return (
    <>
      <div className="mx-auto w-full max-w-screen-md">
        <Breadcrumb className="mb-6">
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to={"/"}>
                  <Home2 size={14} className="-mt-0.5 mr-1 inline" /> Inicio
                </Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to={`/editions/${editionId}/quiz`}>
                  Cuestionario: {application?.edition.name || "Desconocido"}
                </Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>
                Dimensión: {dimension ? dimension.name : "Desconocida"}
              </BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>

        <QuizBanners className="my-6" />

        {dimension && (
          <>
            <QuizDimensionHeader
              title={dimension.name}
              description={dimension.description}
              icon={dimension.icon}
              color={dimension.color}
              percent={dimension.completedPercentage}
              scoreProportion={dimension.scoreProportion}
              completedTopics={dimension.completedTopicsCount}
              totalTopics={dimension.topicsCount}
              answeredQuestions={dimension.respondedQuestionsCount}
              totalQuestions={dimension.totalQuestionsCount}
              showWeight={dimension.dimension !== "COMPANY_INFORMATION"}
            />

            <div className="my-6 text-xl font-semibold text-[#13171B]">
              Temas
            </div>

            {Array.isArray(dimension.topics) && (
              <div className="flex flex-col gap-5">
                {dimension.topics.map((topic, index) => (
                  <TopicAccordionItem
                    key={topic.topic}
                    topic={topic}
                    order={index + 1}
                    showEvidences={dimension.group !== "GENERAL_INFORMATION"}
                    disabled={quiz?.status !== "READY"}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
};

export default QuizDimensionPage;
